/**
 * The concept fields of a concept-test launch, including each concept's uploaded image (spec 83).
 *
 * Images are redrawn at display size before they travel, so a launch stays small however many concepts it
 * has. A concept whose image cannot be read travels without one; it never gets a stand-in image.
 */

import { mapWithConcurrency } from '../../utils/mapWithConcurrency';

export interface ConceptImageSource {
  conceptStr: string;
  imageDataUrl: string | null;
}

export type ShrinkImage = (dataUrl: string, conceptCount: number) => Promise<string | null>;

export type ConceptPayloadFields =
  | { concept_str: string; concept_image?: string }
  | { concepts: Array<{ concept_str: string; image?: string }> };

interface ShrinkDeps {
  loadImage?: (src: string) => Promise<HTMLImageElement>;
  createCanvas?: () => HTMLCanvasElement;
}

/** Longest side by concept count: ~25 MB of images at most, even for a few hundred concepts. */
const SIDE_TIERS: ReadonlyArray<{ maxConcepts: number; maxSide: number }> = [
  { maxConcepts: 20, maxSide: 1200 },
  { maxConcepts: 100, maxSide: 800 },
];
const SMALLEST_MAX_SIDE = 512;
const OUTPUT_TYPE = 'image/jpeg';
const OUTPUT_QUALITY = 0.82;
// JPEG has no transparency; without a fill, a transparent pack shot renders on black.
const BACKGROUND_FILL = '#ffffff';
// Decoding holds a full-size bitmap per image, so hundreds must not decode at once.
const SHRINK_CONCURRENCY = 4;

export const maxImageSideFor = (conceptCount: number): number =>
  SIDE_TIERS.find((tier) => conceptCount <= tier.maxConcepts)?.maxSide ?? SMALLEST_MAX_SIDE;

const loadImageElement = (src: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('image could not be decoded'));
    img.src = src;
  });

export async function shrinkConceptImage(
  dataUrl: string,
  conceptCount: number,
  {
    loadImage = loadImageElement,
    createCanvas = () => document.createElement('canvas'),
  }: ShrinkDeps = {},
): Promise<string | null> {
  try {
    const img = await loadImage(dataUrl);
    if (!img.width || !img.height) return null;
    const scale = Math.min(1, maxImageSideFor(conceptCount) / Math.max(img.width, img.height));
    const canvas = createCanvas();
    canvas.width = Math.max(1, Math.round(img.width * scale));
    canvas.height = Math.max(1, Math.round(img.height * scale));
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.fillStyle = BACKGROUND_FILL;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    const shrunk = canvas.toDataURL(OUTPUT_TYPE, OUTPUT_QUALITY);
    return shrunk.startsWith(`data:${OUTPUT_TYPE}`) ? shrunk : null;
  } catch (error) {
    console.warn('Concept image could not be prepared; launching without it:', error);
    return null;
  }
}

export async function buildConceptPayloadFields(
  concepts: ConceptImageSource[],
  shrink: ShrinkImage = shrinkConceptImage,
): Promise<ConceptPayloadFields> {
  const count = concepts.length;
  const images = await mapWithConcurrency(concepts, SHRINK_CONCURRENCY, (c) =>
    c.imageDataUrl ? shrink(c.imageDataUrl, count) : Promise.resolve(null),
  );

  if (count === 1) {
    const [image] = images;
    return { concept_str: concepts[0].conceptStr, ...(image ? { concept_image: image } : {}) };
  }
  return {
    concepts: concepts.map((c, i) => ({
      concept_str: c.conceptStr,
      ...(images[i] ? { image: images[i] as string } : {}),
    })),
  };
}
